// Atalhos de forma (espec. 8.3): os três botões e os campos do atalho ativo, mais a
// tradução atalho → perfil. Apresentacional: recebe o formulário e devolve patches;
// a validação e a tabela visível ficam com o `RuleEditorOffcanvas`.
import { bpsToPercentInput, integerInput, percentInputToBps } from "./format";
import {
  downPaymentPlusInstallments,
  equalInstallments,
  offsetRangeFor,
} from "./profile";
import { UNIT_OPTIONS } from "./RuleProfileTable";

export const SHORTCUTS = Object.freeze([
  { value: "equal", label: "Parcelas iguais" },
  { value: "down_payment", label: "Entrada + parcelas" },
  { value: "custom", label: "Personalizado" },
]);

// No cronograma atual (revisão de 2026-10-07) a primeira opção é ajustar os meses da
// referência, antes dos atalhos; ela é aberta por padrão quando o alvo tem dados programados.
export const MONTHS_SHORTCUT = Object.freeze({ value: "months", label: "Ajustar meses" });
export const REFERENCE_SHORTCUTS = Object.freeze([MONTHS_SHORTCUT, ...SHORTCUTS]);

const unitOfForm = (form) => form.unit ?? "months";

/** Atalho + formulário → linhas do perfil. Lança `ProfileError` em forma impossível. */
export const buildShortcutProfile = (shortcut, form) => {
  const unit = unitOfForm(form);
  if (shortcut === "equal") {
    return equalInstallments({
      installments: form.installments,
      startOffset: form.startOffset,
      unit,
    });
  }
  return downPaymentPlusInstallments({
    downPaymentBps: form.downPaymentBps,
    downPaymentOffset: form.downPaymentOffset,
    installments: form.installments,
    startOffset: form.startOffset,
    unit,
  });
};

// Os limites do validador viram limites do campo, por unidade: o cliente vê a faixa
// aceita antes de errar, e as setas do input não passam dela (D6).
const offsetRangeStrings = (unit) => {
  const { min, max } = offsetRangeFor(unit);
  return { min: String(min), max: String(max) };
};

const UNIT_SUFFIX = Object.freeze({
  months: "meses",
  calendar_days: "dias corridos",
  business_days: "dias úteis",
});

const numberValue = (value) => (Number.isFinite(value) ? value : "");

// O início do novo cronograma é o primeiro campo da grade em TODOS os atalhos (inclusive o
// "Personalizado", que não tem os outros campos): a defasagem de cada linha conta dali.
// `startField` chega pronto do editor, que é dono do estado do início.
const RuleShortcutFields = ({
  shortcut, form, errorId, onPick, onPatch, startField = null, shortcuts = SHORTCUTS,
}) => {
  const unit = unitOfForm(form);
  const range = offsetRangeStrings(unit);
  return (
    <>
      <div className="ampli-rule-editor__shortcuts" role="group" aria-label="Atalhos de forma">
        {shortcuts.map((option) => (
          <button
            key={option.value}
            type="button"
            aria-pressed={shortcut === option.value}
            className={shortcut === option.value ? "active" : undefined}
            onClick={() => onPick(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className="ampli-rule-editor__shortcut-fields">
        {startField}
        {shortcut === "custom" || shortcut === "months" ? null : (
          <>
            <label className="ampli-rule-editor__field">
              <span className="card-label">Unidade da defasagem</span>
              <select
                className="default-input"
                aria-label="Unidade da defasagem"
                value={unit}
                onChange={(event) => onPatch({ unit: event.target.value })}
              >
                {UNIT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            {shortcut === "down_payment" ? (
              <>
                <label className="ampli-rule-editor__field">
                  <span className="card-label">Entrada (%)</span>
                  <input
                    type="number"
                    className="default-input"
                    aria-label="Percentual da entrada"
                    aria-describedby={errorId}
                    value={bpsToPercentInput(form.downPaymentBps)}
                    onChange={(event) => onPatch({
                      downPaymentBps: percentInputToBps(event.target.value),
                    })}
                  />
                </label>
                <label className="ampli-rule-editor__field">
                  <span className="card-label">Entrada em</span>
                  <input
                    type="number"
                    className="default-input"
                    aria-label="Entrada em"
                    min={range.min}
                    max={range.max}
                    value={numberValue(form.downPaymentOffset)}
                    onChange={(event) => onPatch({
                      downPaymentOffset: integerInput(event.target.value),
                    })}
                  />
                  <span className="ampli-rule-editor__hint">{`${UNIT_SUFFIX[unit]} do início; negativo = antes`}</span>
                </label>
              </>
            ) : null}
            <label className="ampli-rule-editor__field">
              <span className="card-label">Número de parcelas</span>
              <input
                type="number"
                min="1"
                className="default-input"
                aria-label="Número de parcelas"
                value={numberValue(form.installments)}
                onChange={(event) => onPatch({ installments: integerInput(event.target.value) })}
              />
            </label>
            <label className="ampli-rule-editor__field">
              <span className="card-label">1ª parcela em</span>
              <input
                type="number"
                className="default-input"
                aria-label="Primeira parcela em"
                min={range.min}
                max={range.max}
                value={numberValue(form.startOffset)}
                onChange={(event) => onPatch({ startOffset: integerInput(event.target.value) })}
              />
              <span className="ampli-rule-editor__hint">{`${UNIT_SUFFIX[unit]} do início; 0 = no início`}</span>
            </label>
          </>
        )}
      </div>
    </>
  );
};

export default RuleShortcutFields;
